import { API_BASE_URL } from '../config';
import React, { useEffect, useState } from 'react';
import { COLOR_PALETTE, DEFAULT_APPEARANCE, THEME_NAMES, themeByName } from '../utils/clinicTheme';

const ThemeSection = () => {
  const [theme, setTheme] = useState(DEFAULT_APPEARANCE.theme);
  const [prescriptionColor, setPrescriptionColor] = useState(DEFAULT_APPEARANCE.prescriptionColor);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/clinic/appearance`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    }).then(async (response) => {
      if (!response.ok) {
        setError('Could not load the theme.');
        return;
      }
      const data = await response.json();
      if (THEME_NAMES.includes(data.theme)) setTheme(data.theme);
      if (/^#[0-9a-fA-F]{6}$/.test(data.prescriptionColor || '')) {
        setPrescriptionColor(data.prescriptionColor.toLowerCase());
      }
    }).catch(() => setError('Could not load the theme.'));
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/clinic/appearance`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ theme, prescriptionColor })
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.message || 'Could not save the theme.');
        return;
      }
      const saved = {
        theme: data.theme || theme,
        prescriptionColor: data.prescriptionColor || prescriptionColor
      };
      setTheme(saved.theme);
      setPrescriptionColor(saved.prescriptionColor);
      window.dispatchEvent(new CustomEvent('clinic-theme', { detail: saved }));
      setMessage('Saved. The screens and the next prescription use these colors.');
    } catch (err) {
      setError('Could not save the theme.');
    } finally {
      setSaving(false);
    }
  };

  const selected = themeByName(theme);

  return (
    <div>
      <p style={{ marginTop: 0, color: '#495057' }}>
        Default keeps the current clinic look. The other themes recolor the sign-in screen and dashboards. The prescription color is chosen separately.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
        {THEME_NAMES.map((name) => {
          const item = themeByName(name);
          const active = theme === name;
          return (
            <button
              key={name}
              type="button"
              onClick={() => setTheme(name)}
              style={{
                textAlign: 'left',
                padding: '0.75rem',
                borderRadius: '8px',
                border: active ? `2px solid ${item.primary}` : '1px solid #ddd',
                background: '#fff',
                cursor: 'pointer'
              }}
            >
              <span style={{ display: 'flex', gap: '0.35rem', marginBottom: '0.5rem' }}>
                <span style={{ width: 18, height: 18, borderRadius: 4, background: item.primary }} />
                <span style={{ width: 18, height: 18, borderRadius: 4, background: item.accent }} />
                <span style={{ width: 18, height: 18, borderRadius: 4, background: item.wash, border: '1px solid #ddd' }} />
              </span>
              <strong style={{ color: item.primary }}>{item.label}</strong>
              <div style={{ color: '#6c757d', fontSize: '0.85rem' }}>{item.description}</div>
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', background: '#fff' }}>
        <h4 style={{ marginTop: 0 }}>Prescription color</h4>
        <p style={{ color: '#495057' }}>This color is printed on the prescription. Pick a swatch or choose any color.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
          {COLOR_PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={color}
              onClick={() => setPrescriptionColor(color)}
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                background: color,
                border: prescriptionColor === color ? '3px solid #212529' : '1px solid #ccc',
                cursor: 'pointer'
              }}
            />
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '0.5rem' }}>
            Custom
            <input
              type="color"
              value={prescriptionColor}
              onChange={(event) => setPrescriptionColor(event.target.value)}
              aria-label="Prescription color"
            />
          </label>
        </div>
        <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '6px', background: selected.wash, borderLeft: `6px solid ${prescriptionColor}` }}>
          <strong style={{ color: prescriptionColor }}>PRESCRIPTION</strong>
          <div style={{ color: '#495057', fontSize: '0.9rem' }}>Preview of the print accent</div>
        </div>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving}
        style={{ marginTop: '1rem', padding: '10px 16px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: saving ? 'not-allowed' : 'pointer' }}
      >
        {saving ? 'Saving...' : 'Save theme'}
      </button>
      {message && <p style={{ color: '#198754' }}>{message}</p>}
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
    </div>
  );
};

export default ThemeSection;
